import BlockIcon from '@mui/icons-material/Block';
import BusinessIcon from '@mui/icons-material/Business';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { getWizardBlacklists } from '../../campaign-wizard.api';
import { fmtNumber } from '../../campaign-wizard.logic';
import { WizardBlacklistList } from '../../campaign-wizard.types';
import { wz } from '../../components/wizard-tokens';
import { MiSpinner, WizCard } from '../../components/wizard-ui';
import type { WizardController } from '../../use-campaign-wizard';
import { isListOn, listCountLabel, missingListDefaults, withNewList } from './candidates-logic';
import NewBlacklistDialog from './new-blacklist-dialog';

/**
 * Zweite Karte von Schritt 2: Blacklist. Zeigt nur die tatsächlich vorhandenen Blacklists; eine neue
 * entsteht im Fenster „Neue Blacklist anlegen“ (die reguläre Anlage-Maske) und ist danach gewählt.
 */
const BlacklistCard: React.FC<{ wizard: WizardController }> = ({ wizard }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { state, patch } = wizard;
  const [lists, setLists] = useState<WizardBlacklistList[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    let cancelled = false;
    getWizardBlacklists()
      .then(res => {
        if (cancelled) return;
        setLists(res.lists);
        const defaults = missingListDefaults(state.globalBlacklists, res.lists);
        if (defaults) patch({ globalBlacklists: defaults });
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(true);
          setLists([]);
        }
      });
    return () => {
      cancelled = true;
    };
    // Die Listen werden einmal beim Öffnen geladen.
  }, []);

  const toggle = (list: WizardBlacklistList) =>
    patch({ globalBlacklists: { ...state.globalBlacklists, [list.id]: !isListOn(state, list) } });

  /** Dialog ist zu: Listen neu laden, die neue Blacklist wählen. Der Wizard-Zustand bleibt erhalten. */
  const onCreated = async (id: string | undefined, name: string, type: string) => {
    setCreateOpen(false);
    try {
      const res = await getWizardBlacklists();
      setLists(res.lists);
      setLoadError(false);
      const created =
        (id ? res.lists.find(l => l.id === id) : undefined) ??
        // Ohne ID: Name UND Typ (gleichnamige Firmen-/Kontakt-Listen sind möglich).
        res.lists.find(l => l.name === name && l.type === type);
      patch({
        globalBlacklists: withNewList(stateRef.current.globalBlacklists, res.lists, created),
      });
    } catch {
      setLoadError(true);
    }
  };

  return (
    <WizCard gap={18}>
      <div className={classes.head}>
        <div className={classes.headText}>
          <div className={classes.headTitle}>
            <BlockIcon sx={{ fontSize: 20, color: wz.primary }} />
            {t('wizCandBlacklistTitle')}
          </div>
          <div className={classes.headDesc}>{t('wizCandBlacklistDesc')}</div>
        </div>
      </div>
      <div className={classes.section}>
        <div className={classes.sectionTitle}>{t('wizCandLists')}</div>
        {lists === null ? (
          <div className={classes.muted}>
            <MiSpinner size={16} />
            {t('wizCandGlobalListsLoading')}
          </div>
        ) : loadError ? (
          <div className={classes.error}>{t('wizCandGlobalListsError')}</div>
        ) : (
          <div className={classes.lists}>
            {lists.length === 0 ? <div className={classes.muted}>{t('wizCandListsEmpty')}</div> : null}
            {lists.map(list => {
              const on = isListOn(state, list);
              return (
                <button
                  key={list.id}
                  type="button"
                  className={cx(classes.list, on ? 'on' : undefined)}
                  onClick={() => toggle(list)}
                  aria-pressed={on}
                >
                  {on ? (
                    <CheckBoxIcon sx={{ fontSize: 20, color: wz.primary }} />
                  ) : (
                    <CheckBoxOutlineBlankIcon sx={{ fontSize: 20, color: wz.dashed }} />
                  )}
                  {list.type === 'company' ? (
                    <BusinessIcon sx={{ fontSize: 18, color: wz.muted }} />
                  ) : (
                    <PersonIcon sx={{ fontSize: 18, color: wz.muted }} />
                  )}
                  <div className={classes.listText}>
                    <div style={{ fontSize: 14 }}>{list.name}</div>
                    <div style={{ fontSize: 12, color: wz.muted }}>
                      {listCountLabel(
                        list,
                        fmtNumber,
                        t('wizCandCountCompanies'),
                        t('wizCandCountContacts'),
                        t('wizCandCountCompany'),
                        t('wizCandCountContact')
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <button
        type="button"
        className={classes.create}
        onClick={() => setCreateOpen(true)}
        data-testid="wiz-new-blacklist"
      >
        <AddIcon sx={{ fontSize: 18 }} />
        {t('wizCandNewBlacklist')}
      </button>
      <NewBlacklistDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={onCreated}
      />
    </WizCard>
  );
};

const useStyles = makeStyles()({
  head: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 },
  headText: { display: 'flex', flexDirection: 'column', gap: 4 },
  headTitle: { fontSize: 17, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 },
  headDesc: { fontSize: 14, color: wz.muted },
  create: {
    fontSize: 14,
    color: wz.primary,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    alignSelf: 'flex-start',
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
    fontWeight: 600,
  },
  section: { display: 'flex', flexDirection: 'column', gap: 8 },
  sectionTitle: { fontSize: 14, fontWeight: 700 },
  lists: { display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 10 },
  list: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    borderRadius: 10,
    padding: '10px 12px',
    cursor: 'pointer',
    border: `1px solid ${wz.border}`,
    background: '#FFF',
    textAlign: 'left',
    fontFamily: 'inherit',
    color: wz.text,
    '&.on': { border: `1.5px solid ${wz.primary}` },
  },
  listText: { display: 'flex', flexDirection: 'column' },
  muted: { fontSize: 13, color: wz.muted, display: 'flex', alignItems: 'center', gap: 6 },
  error: { fontSize: 13, color: wz.redFg, width: '100%' },
});

export default BlacklistCard;
